import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@lipaflow/shared'

type PropertyOption = { id: string; name: string }

const PER_PAGE = 20

/**
 * The buildings a building-scoped person works in: one picker in line with
 * the rest of the invite, the chosen ones listed under it. A search box only
 * appears when there are more buildings than the picker lists.
 */
export default function RentalInviteScopeFields({ role, requiredScope, onChange, initialScopes }: {
  role: string
  requiredScope?: string | null
  onChange: (scopes: Record<string, string[]>) => void
  initialScopes?: Record<string, string[]>
}) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<PropertyOption[]>(
    () => (initialScopes?.property ?? []).map((id) => ({ id, name: id })),
  )
  const needsProperty = requiredScope === 'property' || (!requiredScope && ['caretaker', 'property_manager', 'leasing_agent'].includes(role))
  const properties = useQuery({
    queryKey: ['rental-invite-properties', search],
    queryFn: async () => (await api.get<{ items: PropertyOption[]; total?: number }>('/rental/properties', {
      params: { search, page: 1, per_page: PER_PAGE },
    })).data,
    enabled: needsProperty,
  })
  const options = properties.data?.items ?? []
  const many = search !== '' || (properties.data?.total ?? options.length) > PER_PAGE
  const nameOf = (item: PropertyOption) => options.find((option) => option.id === item.id)?.name ?? item.name

  useEffect(() => {
    onChange(needsProperty && selected.length ? { property: selected.map((item) => item.id) } : {})
  }, [needsProperty, selected, onChange])

  if (!needsProperty) return null
  return <div className="w-56">
    <label className="block text-sm font-medium text-ink" htmlFor="invite-property">Buildings</label>
    {many && <input type="search" aria-label="Search buildings" value={search} onChange={(event) => setSearch(event.target.value)}
      placeholder="Search buildings" className="mt-1 block w-full px-3 py-2 border border-hairline rounded-button" />}
    <select id="invite-property" aria-label="Add assigned property" value="" onChange={(event) => {
      const option = options.find((item) => item.id === event.target.value)
      if (option && !selected.some((item) => item.id === option.id)) setSelected((current) => [...current, option])
    }} className="mt-1 block w-full px-3 py-2 border border-hairline rounded-button">
      <option value="">{selected.length ? 'Add another building' : 'Choose a building'}</option>
      {options.filter((item) => !selected.some((chosen) => chosen.id === item.id))
        .map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
    </select>
    {selected.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">
      {selected.map((item) => <button key={item.id} type="button" aria-label={`Remove ${nameOf(item)}`}
        onClick={() => setSelected((current) => current.filter((entry) => entry.id !== item.id))}
        className="rounded-full bg-accent-soft px-2.5 py-0.5 text-caption text-accent hover:bg-accent/15">{nameOf(item)} ×</button>)}
    </div>}
  </div>
}
